/** The string formats `formatValueString` understands. */
export type FormatValueStringFormat = 'birthday' | 'date' | 'email' | 'uri';

export type FormatValueStringOptions = {
    dateTimeFormatOptions?: Intl.DateTimeFormatOptions;
    locale: string;
    numberFormatOptions?: Intl.NumberFormatOptions;
    stringFormat?: FormatValueStringFormat;
    /** Only the yes/no labels are translated, so `t` from any namespace fits. */
    t: (key: 'yes_no__true' | 'yes_no__false') => string;
    value?: unknown;
};

/**
 * The text `FormatValue` renders, without the markup — so anything that needs
 * the displayed value as a string (clipboard, export, aria-label) cannot drift
 * from what is on screen.
 *
 * Links are the one deliberate difference: they come back as the raw address
 * rather than the shortened link text, because that is what a reader pasting
 * a URL or an email wants.
 */
export function formatValueString({
    dateTimeFormatOptions = { dateStyle: 'medium' },
    locale,
    numberFormatOptions = {},
    stringFormat,
    t,
    value,
}: FormatValueStringOptions): string {
    if (value === undefined || value === null || value === '') {
        return '';
    }
    if (value instanceof Date) {
        return new Intl.DateTimeFormat(locale, dateTimeFormatOptions).format(value);
    }
    if (typeof value === 'boolean') {
        return value ? t('yes_no__true') : t('yes_no__false');
    }
    if (typeof value === 'number') {
        return new Intl.NumberFormat(locale, numberFormatOptions).format(value);
    }
    if (typeof value === 'string') {
        if (stringFormat === 'date' || stringFormat === 'birthday') {
            return formatIsoDateString(value, { dateTimeFormatOptions, locale, stringFormat }) ?? value;
        }
        return value;
    }
    // eslint-disable-next-line @typescript-eslint/no-base-to-string -- this is a fallback
    return String(value);
}

type FormatIsoDateOptions = {
    dateTimeFormatOptions: Intl.DateTimeFormatOptions;
    locale: string;
    stringFormat: 'birthday' | 'date';
};

/** Formats an ISO date held as a string, or nothing if it isn't one. */
function formatIsoDateString(
    value: string,
    { dateTimeFormatOptions, locale, stringFormat }: FormatIsoDateOptions,
): string | undefined {
    if (!/^\s*\d\d\d\d-\d\d-\d\d(T\d\d:\d\d(:\d\d(\.\d{1,3})?)?Z?)?\s*$/.test(value)) {
        return undefined;
    }

    let dateStr = value.trim();
    if (!dateStr.endsWith('Z')) {
        dateStr += 'Z';
    }
    const dateValue = new Date(dateStr);
    const hasTimeComponent = dateStr.includes('T');

    let options = dateTimeFormatOptions;
    if (stringFormat === 'birthday') {
        const hasYear = !value.startsWith('0000');
        options = {
            day: '2-digit',
            month: '2-digit',
            year: hasYear ? 'numeric' : undefined,
            timeZone: 'UTC', // don't shift birthday with DST
        };
    } else if (hasTimeComponent) {
        // don't shift birthday with DST
        options = { ...options, timeZone: 'UTC' };
    }

    return new Intl.DateTimeFormat(locale, options).format(dateValue);
}
